import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  CourseParticipant,
  DozentService,
} from '../../../services/dozent.service';
import { participantPseudonym } from '../../../helpers/participant-pseudonym';

interface OwnExercise {
  id: string;
  title: string;
  exercise_type: string;
  description?: string;
  is_published?: boolean;
  auto_process_submissions?: boolean;
}

interface OwnDozentBot {
  id: string;
  name: string;
  visual_name?: string;
  /** 'alpha_dozent' | 'alpha_tutor' | 'alpha_flexible' — absent on older
   *  backends, which listed twins only. */
  template?: string;
}

@Component({
  selector: 'app-dispatch-exercise-dialog',
  templateUrl: './dispatch-exercise-dialog.component.html',
  styleUrl: './dispatch-exercise-dialog.component.scss',
})
export class DispatchExerciseDialogComponent implements OnInit {
  @Input() courseId!: string;
  @Input() participants: CourseParticipant[] = [];
  @Input() onDispatched: () => void = () => {};
  /** Only offer published exercises (participant-detail assign flow). */
  @Input() publishedOnly = false;
  /** Pre-tick all dispatchable participants (single-learner assign flow). */
  @Input() preselectParticipants = false;

  exercises: OwnExercise[] = [];
  bots: OwnDozentBot[] = [];

  loading = true;
  saving = false;

  selectedExerciseId: string | null = null;
  selectedBotId: string | null = null;
  instructions = '';
  dueDate: string | null = null;
  scheduledPublishAt: string | null = null;
  selectedParticipantIds = new Set<string>();

  constructor(
    protected dialogRef: NbDialogRef<DispatchExerciseDialogComponent>,
    private dozentService: DozentService,
    private toastrService: NbToastrService,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    Promise.all([
      this.dozentService.listOwnExercises().toPromise(),
      this.dozentService.listOwnDozentBots().toPromise(),
    ])
      .then(([exercises, bots]: any) => {
        this.exercises = (exercises ?? []).filter(
          (e: OwnExercise) => !this.publishedOnly || e.is_published
        );
        this.bots = bots ?? [];
        if (this.bots.length === 1) {
          this.selectedBotId = this.bots[0].id;
        }
        if (this.preselectParticipants) {
          this.participants
            .filter((p) => this.isDispatchable(p))
            .forEach((p) => this.selectedParticipantIds.add(p.learner_user_id!));
        }
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
        this.toastrService.danger(this.t.instant('toasts.error'), '');
      });
  }

  isDispatchable(p: CourseParticipant): boolean {
    return !!p.learner_user_id;
  }

  toggleParticipant(p: CourseParticipant) {
    if (!this.isDispatchable(p)) return;
    const id = p.learner_user_id!;
    if (this.selectedParticipantIds.has(id)) {
      this.selectedParticipantIds.delete(id);
    } else {
      this.selectedParticipantIds.add(id);
    }
  }

  toggleAll() {
    const dispatchable = this.participants.filter((p) => this.isDispatchable(p));
    if (this.selectedParticipantIds.size === dispatchable.length) {
      this.selectedParticipantIds.clear();
    } else {
      this.selectedParticipantIds.clear();
      dispatchable.forEach((p) => this.selectedParticipantIds.add(p.learner_user_id!));
    }
  }

  participantLabel(p: CourseParticipant): string {
    return participantPseudonym(p);
  }

  botDisplayName(bot: OwnDozentBot): string {
    return (bot.visual_name || bot.name || '').trim();
  }

  botTypeKey(bot: OwnDozentBot): string {
    switch (bot.template) {
      case 'alpha_tutor':
        return 'pages.dozent.participants.botTypeTutor';
      case 'alpha_flexible':
        return 'pages.dozent.participants.botTypeFlex';
      default:
        return 'pages.dozent.participants.botTypeTwin';
    }
  }

  /** Missing template = old backend (twin-only list) → treat as twin. */
  get selectedBotIsTwin(): boolean {
    const bot = this.bots.find((b) => b.id === this.selectedBotId);
    return !bot?.template || bot.template === 'alpha_dozent';
  }

  /** Non-twin dispatch has no human feedback channel — only exercises the
   *  AI grades and releases automatically stay selectable (mirrors the
   *  backend guard). */
  get selectableExercises(): OwnExercise[] {
    if (!this.selectedBotId || this.selectedBotIsTwin) return this.exercises;
    return this.exercises.filter((e) => e.auto_process_submissions !== false);
  }

  onBotSelected(botId: string | null) {
    this.selectedBotId = botId;
    if (
      this.selectedExerciseId &&
      !this.selectableExercises.some((e) => e.id === this.selectedExerciseId)
    ) {
      this.selectedExerciseId = null;
    }
  }

  botExternalName(bot: OwnDozentBot): string | null {
    const externalName = (bot.visual_name || '').trim();
    return externalName || null;
  }

  botInternalName(bot: OwnDozentBot): string | null {
    const internalName = (bot.name || '').trim();
    return internalName || null;
  }

  canSubmit(): boolean {
    return (
      !this.saving &&
      !!this.selectedExerciseId &&
      !!this.selectedBotId &&
      this.selectedParticipantIds.size > 0
    );
  }

  submit() {
    if (!this.canSubmit()) return;
    this.saving = true;
    this.dozentService
      .dispatchExercise(this.courseId, {
        exercise_id: this.selectedExerciseId!,
        bot_id: this.selectedBotId!,
        participant_user_ids: Array.from(this.selectedParticipantIds),
        instructions: this.instructions || undefined,
        due_date: this.dueDate || undefined,
        scheduled_publish_at: this.localDateTimeToIso(this.scheduledPublishAt),
      })
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.t.instant('pages.dozent.participants.exerciseDispatched'),
            ''
          );
          this.onDispatched();
          this.dialogRef.close();
        },
        error: () => {
          this.saving = false;
          this.toastrService.danger(this.t.instant('toasts.error'), '');
        },
      });
  }

  close() {
    this.dialogRef.close();
  }

  private localDateTimeToIso(value: string | null): string | undefined {
    if (!value) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toISOString();
  }
}
